import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  inject
} from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subject, Subscription, takeUntil, switchMap, of } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { CommonModule } from '@angular/common';
import { buildCategoryFilterData, buildLegendSyncData, matchValue, MatchMode } from '../../utils/filter.utils';
import { getContrastingTextColor } from '../../utils/color.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-stacked-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './stacked-horizontal-bar-chart.component.html',
  styleUrls: ['./stacked-horizontal-bar-chart.component.scss']
})
export class StackedHorizontalBarChartComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('chartContainer', { static: true })
  chartContainer!: ElementRef;

  @Input() chartData: any;
  private _data: any;
  @Input('data')
  set data(v: any) {
    this._data = v;
    if (v && v.series && Array.isArray(v.series)) {
      this.chartData = v;
      if (this.chartInstance) this.initChart(this.chartData);
    }
  }
  get data(): any { return this._data; }

  chartInstance!: echarts.ECharts;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: any;
  protected unSubscribe$: Subject<void> = new Subject();
  // Scroll / slice support
  protected allCategories: string[] = [];
  protected visibleCategories: string[] = [];
  protected allSeriesData: { cumminsValues: number[]; nonCumminsValues: number[]; cumminsLabel: string; nonCumminsLabel: string; cumminsColor: string; nonCumminsColor: string } | null = null;
  protected visibleSeries: any[] = [];
  protected sliderIndex = 0;
  protected maxVisibleBars = 5;
  protected barHeight = 50;
  protected visibleHeight = 250;
  protected isScrollbarDragging = false;
  protected scrollbarStartY = 0;
  protected scrollStartOffset = 0;
  protected dragHandler: ((e: MouseEvent) => void) | null = null;
  protected upHandler: (() => void) | null = null;

  // Cross-filtering state
  selectedCategories: string[] = [];
  selectedLegends: string[] = [];

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService
  ) {}

  ngAfterViewInit(): void {
    this.chartInstance = echarts.init(this.chartContainer.nativeElement, 'dvm');
    if (this.chartData) {
      this.initChart(this.chartData);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.syncVisibleHeightFromContainer();
      this.chartInstance?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.chartInstance) return;
      const el = this.chartContainer?.nativeElement;
      if (!el) return;
      this.chartInstance.dispose();
      this.chartInstance = echarts.init(el, 'dvm');
      this.initChart(this.lastChartData);
    });

    // Listen to global filter changes and load data
    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters: any) => {
          if (!this.chartFilterHandler.shouldReload(filters, this._data?.widgetId)) {
            // Handle legendSync (visual dimming from other charts)
            if (filters?.legendSync) {
              this.handleExternalLegendSync(filters);
            }
            return of(null);
          }

          // Handle legendSync + reload
          if (filters?.legendSync) {
            this.handleExternalLegendSync(filters);
            if (!filters.isCategoryFilter) {
              return this.loadAndRenderChart(filters);
            }
          }

          // Only clear selection on global dropdown change (no widgetId)
          if (!filters?.widgetId) {
            this.selectedCategories = [];
            this.selectedLegends = [];
          }

          return this.loadAndRenderChart(filters);
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res) return;
          const chartData = res?.data || res;
          if (chartData) this.initChart(chartData);
        },
        error: () => { /* ignore */ }
      });

    // Fetch initial data once
    try {
      const initialFilters = this.globalFilterService.getCurrentFilters();
      this.loadAndRenderChart(initialFilters).pipe(takeUntil(this.unSubscribe$)).subscribe({
        next: (res: any) => {
          const chartData = this.extractChartData(res);
          if (chartData) this.initChart(chartData);
        },
        error: () => { /* ignore */ }
      });
    } catch (e) {
      // ignore
    }
  }

  protected loadAndRenderChart(filters?: any) {
    const payloadFilters: any = { ...(filters || {}) };
    if ((!payloadFilters.requestParams || Object.keys(payloadFilters.requestParams || {}).length === 0) && this._data?.requestData) {
      payloadFilters.requestParams = { ...this._data.requestData };
    }
    payloadFilters.useQueryParams = this._data?.useQueryParams || payloadFilters.useQueryParams || false;

    const apiUrl = this._data?.apiUrl || this.data?.apiUrl;
    const method = this._data?.method || this.data?.method || 'post';

    return this.configService.getConfig(apiUrl, this._data || this.data, payloadFilters, method).pipe(takeUntil(this.unSubscribe$));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chartInstance) {
      this.initChart(this.chartData);
    }
  }

  protected resolveWidgetHeight(): void {
    const resolvedHeightPx = this.responsiveLayoutService.resolveHeightToPixels(this._data || this.data);
    if (resolvedHeightPx && resolvedHeightPx > 0) {
      this.visibleHeight = resolvedHeightPx;
    }
    this.syncVisibleHeightFromContainer();
  }

  getContainerHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this._data || this.data);
  }

  protected syncVisibleHeightFromContainer(): void {
    const containerHeight = this.chartContainer?.nativeElement?.clientHeight;
    if (containerHeight && containerHeight > 0) {
      this.visibleHeight = containerHeight;
    }
  }

  /**
   * Extract chart data using dataKey from widget config (new platform structure),
   * then fall back to legacy formats.
   */
  protected extractChartData(res: any): any {
    const dataKey = this._data?.dataKey || this.data?.dataKey;
    // New platform: unwrapped response has { [dataKey]: [...] }
    if (dataKey && res?.[dataKey] !== undefined) {
      return res;
    }
    // Legacy: res.data or direct res
    if (res?.data) return res.data;
    return res;
  }

  initChart(data: any): void {

    if (!data) return;
    this.lastChartData = data;
    let barArray: any[] | undefined;
    try {
      const dk = this._data?.dataKey || this.data?.dataKey;
      barArray = (dk && Array.isArray(data?.[dk])) ? data[dk] : data?.responseList?.[0]?.data?.[0]?.bar || data?.bar || data?.data?.[0]?.bar;
    } catch (e) {
      barArray = undefined;
    }

    if (Array.isArray(barArray) && barArray.length) {
      const displacementKey = (this._data && (this._data.displacementKey || this._data.displacement_key))
        || (this.data && (this.data.displacementKey || this.data.displacement_key));
      const kindKey = (this._data && (this._data.kindKey || this._data.kind_key))
        || (this.data && (this.data.kindKey || this.data.kind_key));
      const quantityKey = (this._data && (this._data.quantityKey || this._data.quantity_key))
        || (this.data && (this.data.quantityKey || this.data.quantity_key));

      const cumminsValue = (this._data && this._data.cumminsValue) || (this.data && this.data.cumminsValue);
      const nonCumminsValue = (this._data && this._data.nonCumminsValue) || (this.data && this.data.nonCumminsValue);

      const cumminsLabel = (this._data && this._data.cumminsLabel) || (this.data && this.data.cumminsLabel) || cumminsValue;
      const nonCumminsLabel = (this._data && this._data.nonCumminsLabel) || (this.data && this.data.nonCumminsLabel) || nonCumminsValue;

      if (!displacementKey || !kindKey || !quantityKey || !cumminsValue || !nonCumminsValue) {
        console.error('StackedHorizontalBarChartComponent: missing required mapping keys in widget JSON.');
        return;
      }

      const groups = new Map<string, { cummins: number; nonCummins: number }>();

      for (const row of barArray) {
        const dispRaw = row?.[displacementKey];
        const disp = dispRaw == null ? '' : String(dispRaw);
        const kindRaw = row?.[kindKey];
        const kind = kindRaw == null ? '' : String(kindRaw).toLowerCase();
        const qty = Number(row?.[quantityKey] ?? row?.value ?? 0) || 0;

        if (!groups.has(disp)) groups.set(disp, { cummins: 0, nonCummins: 0 });
        const g = groups.get(disp)!;

        const matchCummins = String(cumminsValue).trim().toLowerCase();
        const matchNonCummins = String(nonCumminsValue).trim().toLowerCase();
        const kindNorm = kind.trim().toLowerCase();

        if (matchNonCummins && kindNorm === matchNonCummins) {
          g.nonCummins += qty;
        } else if (matchCummins && kindNorm === matchCummins) {
          g.cummins += qty;
        } else if (matchNonCummins && kindNorm.includes(matchNonCummins)) {
          g.nonCummins += qty;
        } else if (matchCummins && kindNorm.includes(matchCummins)) {
          g.cummins += qty;
        } else {
          if (kindNorm.includes('non')) g.nonCummins += qty;
          else if (kindNorm.includes('cummins')) g.cummins += qty;
          else g.nonCummins += qty;
        }
      }

      const sortOrderRaw = (this._data && (this._data.sortOrder || this._data.sort)) || (this.data && (this.data.sortOrder || this.data.sort)) || 'asc';
      const so = String(sortOrderRaw || '').toLowerCase();
      const descending = so === 'desc' || so === 'dsc';

      const categories = Array.from(groups.keys()).sort((a, b) => {
        const ga = groups.get(a) || { cummins: 0, nonCummins: 0 };
        const gb = groups.get(b) || { cummins: 0, nonCummins: 0 };
        const totalA = Number(ga.cummins || 0) + Number(ga.nonCummins || 0);
        const totalB = Number(gb.cummins || 0) + Number(gb.nonCummins || 0);
        if (!isNaN(totalA) && !isNaN(totalB) && totalA !== totalB) {
          return descending ? totalB - totalA : totalA - totalB;
        }
        const aStr = a == null ? '' : String(a);
        const bStr = b == null ? '' : String(b);
        const na = parseFloat(aStr.replace(/[^0-9.\-\.]*/g, ''));
        const nb = parseFloat(bStr.replace(/[^0-9.\-\.]*/g, ''));
        let cmp = 0;
        if (!isNaN(na) && !isNaN(nb)) cmp = na - nb;
        else cmp = aStr.localeCompare(bStr);
        return descending ? -cmp : cmp;
      });

      const cumminsValues = categories.map(c => groups.get(c)!.cummins || 0);
      const nonCumminsValues = categories.map(c => groups.get(c)!.nonCummins || 0);

      const widgetChartConfig = (this._data && this._data.chartConfig) || (this.data && this.data.chartConfig) || {};
      const cumminsColor = (this._data && this._data.cumminsColor) || (this.data && this.data.cumminsColor) || (widgetChartConfig.barColors && widgetChartConfig.barColors[0]);
      const nonCumminsColor = (this._data && this._data.nonCumminsColor) || (this.data && this.data.nonCumminsColor) || (widgetChartConfig.barColors && widgetChartConfig.barColors[1]);

      // Color consistency
      if (this._data?.enableColorConsistency) {
        const globalCummins = this.chartFilterHandler.getItemColor(cumminsLabel);
        if (globalCummins) { /* use it */ } else if (cumminsColor) {
          this.chartFilterHandler.registerItemColor(cumminsLabel, cumminsColor);
        }
        const globalNonCummins = this.chartFilterHandler.getItemColor(nonCumminsLabel);
        if (globalNonCummins) { /* use it */ } else if (nonCumminsColor) {
          this.chartFilterHandler.registerItemColor(nonCumminsLabel, nonCumminsColor);
        }
      }

      this.allCategories = categories;
      this.barHeight = this._data?.barHeight || 50;
      this.maxVisibleBars = this._data?.chartConfig?.maxVisibleBars || 5;
      this.sliderIndex = 0;
      this.visibleHeight = this.barHeight * Math.min(this.maxVisibleBars, categories.length);
      this.resolveWidgetHeight();
      this.allSeriesData = { cumminsValues, nonCumminsValues, cumminsLabel, nonCumminsLabel, cumminsColor, nonCumminsColor };
      this.updateVisibleData();
      try {
        if (this.chartContainer && this.chartContainer.nativeElement) {
          this.chartContainer.nativeElement.style.height = this.getContainerHeight();
          this.syncVisibleHeightFromContainer();
        }
      } catch (e) {}
      if (this.chartInstance) this.renderChartWithVisibleData(cumminsLabel, nonCumminsLabel, cumminsColor, nonCumminsColor);
      return;
    }

    // Fallback: already in {categories, series} shape
    if (data.series && Array.isArray(data.series)) {
      this.allCategories = data.categories || [];
      this.barHeight = this._data?.barHeight || 50;
      this.maxVisibleBars = this._data?.chartConfig?.maxVisibleBars || 5;
      this.sliderIndex = 0;
      this.visibleHeight = this.barHeight * Math.min(this.maxVisibleBars, this.allCategories.length);
      this.resolveWidgetHeight();
      const s0 = data.series[0] || { data: [] };
      const s1 = data.series[1] || { data: [] };
      const cumminsValues = s0.data || [];
      const nonCumminsValues = s1.data || [];
      const cumminsLabel = s0.name || '';
      const nonCumminsLabel = s1.name || '';
      const widgetChartConfig = (this._data && this._data.chartConfig) || (this.data && this.data.chartConfig) || {};
      const cumminsColor = s0?.itemStyle?.color || s0?.color || (widgetChartConfig.barColors && widgetChartConfig.barColors[0]) || '';
      const nonCumminsColor = s1?.itemStyle?.color || s1?.color || (widgetChartConfig.barColors && widgetChartConfig.barColors[1]) || '';
      this.allSeriesData = { cumminsValues, nonCumminsValues, cumminsLabel, nonCumminsLabel, cumminsColor, nonCumminsColor };
      this.updateVisibleData();
      try {
        if (this.chartContainer && this.chartContainer.nativeElement) {
          this.chartContainer.nativeElement.style.height = this.getContainerHeight();
          this.syncVisibleHeightFromContainer();
        }
      } catch (e) {}
      if (this.chartInstance) this.renderChartWithVisibleData(cumminsLabel, nonCumminsLabel, cumminsColor, nonCumminsColor);
    } else {
      this.chartInstance.setOption({});
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  // --- Scrolling helpers ---
  protected updateVisibleData(): void {
    if (!this.allSeriesData) return;
    if (this.allCategories.length > this.maxVisibleBars) {
      const end = Math.min(this.sliderIndex + this.maxVisibleBars, this.allCategories.length);
      this.visibleCategories = this.allCategories.slice(this.sliderIndex, end);
      this.visibleSeries = [
        { data: this.allSeriesData.cumminsValues.slice(this.sliderIndex, end) },
        { data: this.allSeriesData.nonCumminsValues.slice(this.sliderIndex, end) }
      ];
    } else {
      this.visibleCategories = this.allCategories;
      this.visibleSeries = [
        { data: this.allSeriesData.cumminsValues },
        { data: this.allSeriesData.nonCumminsValues }
      ];
    }
  }

  protected renderChartWithVisibleData(cumminsLabel: string, nonCumminsLabel: string, cumminsColor: string, nonCumminsColor: string): void {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const displayCategories = (this.visibleCategories || []).slice();
    const series0 = (this.visibleSeries[0]?.data || []).slice();
    const series1 = (this.visibleSeries[1]?.data || []).slice();
    const displayReversed = displayCategories.slice().reverse();
    const series0Reversed = series0.slice().reverse();
    const series1Reversed = series1.slice().reverse();

    const widgetChartConfig = (this._data && this._data.chartConfig) || (this.data && this.data.chartConfig) || {};
    const showTooltip = widgetChartConfig?.tooltip?.show !== false;
    const showLabels =
      widgetChartConfig?.showLabels !== false &&
      widgetChartConfig?.showDataLabels !== false &&
      widgetChartConfig?.showBarLabels !== false;
    const labelThresholdPercent = (typeof widgetChartConfig.labelThresholdPercent === 'number') ? widgetChartConfig.labelThresholdPercent : 15;
    const labelMinTotal = (typeof widgetChartConfig.labelMinTotal === 'number') ? widgetChartConfig.labelMinTotal : 1;

    const totalsReversed = series0Reversed.map((v: any, i: number) => {
      const a = Number(v || 0);
      const b = Number(series1Reversed[i] || 0);
      return a + b;
    });

    const xMin = 0;
    const xMax = totalsReversed.length ? Math.max(...totalsReversed) : 0;
    const axisMax = xMax;

    const gridConfig = widgetChartConfig.grid || { left: '3%', right: '80px', top: '10%', bottom: '3%', containLabel: true };

    const option = {
      grid: gridConfig,
      tooltip: {
        show: showTooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
      },
      legend: { data: [cumminsLabel, nonCumminsLabel] },
      xAxis: {
        type: 'value',
        min: xMin,
        max: axisMax,
        axisLabel: {
          formatter: (value: any) => {
            const eps = Math.max(Math.abs(axisMax) * 1e-6, 1e-6);
            if (Math.abs(value - xMin) <= eps || Math.abs(value - axisMax) <= eps) return this.formatLabelValue(value);
            return '';
          }
        }
      },
      yAxis: {
        type: 'category',
        data: displayReversed,
        triggerEvent: true,
      },
      series: [
        {
          name: cumminsLabel,
          type: 'bar',
          stack: 'total',
          label: {
            show: showLabels,
            position: 'inside',
            color: getContrastingTextColor(cumminsColor),
            formatter: (params: any) => {
              const idx = params.dataIndex;
              const a = Number(series0Reversed[idx] || 0);
              const b = Number(series1Reversed[idx] || 0);
              const total = a + b;
              if (total < labelMinTotal) return '';
              const pct = total ? (a / total) * 100 : 0;
              return pct < labelThresholdPercent ? '' : this.formatLabelValue(params.value);
            }
          },
          data: series0Reversed,
          itemStyle: { color: cumminsColor }
        },
        {
          name: nonCumminsLabel,
          type: 'bar',
          stack: 'total',
          label: {
            show: showLabels,
            position: 'inside',
            color: getContrastingTextColor(nonCumminsColor),
            formatter: (params: any) => {
              const idx = params.dataIndex;
              const a = Number(series0Reversed[idx] || 0);
              const b = Number(series1Reversed[idx] || 0);
              const total = a + b;
              if (total < labelMinTotal) return '';
              const pct = total ? (b / total) * 100 : 0;
              return pct < labelThresholdPercent ? '' : this.formatLabelValue(params.value);
            }
          },
          data: series1Reversed,
          itemStyle: { color: nonCumminsColor }
        },
        // Invisible series for total labels
        {
          name: 'Total',
          type: 'bar',
          data: totalsReversed,
          barGap: '-100%',
          itemStyle: { color: 'transparent' },
          tooltip: { show: false },
          silent: true,
          label: {
            show: showLabels,
            position: 'right',
            color: theme.chart?.dataLabelOutside || theme.chart?.textColor || '#0A0A0A',
            formatter: (params: any) => {
              const v = Number(params?.value || 0);
              return v < (labelMinTotal || 0) ? '' : this.formatLabelValue(v);
            }
          }
        }
      ]
    };
    applyRoleVisibilityToOption(option, this.data);
    this.chartInstance.setOption(option, true);

    // Setup click events for cross-filtering
    this.setupChartClickEvent();

    // Re-apply active visual selection after re-render
    if (this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    } else if (this.selectedLegends.length > 0) {
      this.highlightSeriesByNames(this.selectedLegends);
    }
  }

  // ==================== CLICK EVENT SETUP ====================

  protected setupChartClickEvent(): void {
    this.chartInstance.off('click');
    this.chartInstance.on('click', (params: any) => {
      // Handle y-axis label clicks (this is a horizontal chart, y-axis has categories)
      if (params.componentType === 'yAxis') {
        if (this._data?.enableLabelClick) {
          this.handleLabelClick(params.value);
        }
        return;
      }

      // Skip the invisible Total series
      if (params.seriesName === 'Total') return;

      // Bar click → treat as label click (filter by category)
      if (params.componentType === 'series' && this._data?.enableLabelClick) {
        this.handleLabelClick(params.name);
        return;
      }
    });

    // Handle ECharts built-in legend click
    this.chartInstance.off('legendselectchanged');
    this.chartInstance.on('legendselectchanged', (params: any) => {
      if (!this._data?.enableLegendClick) return;
      // Prevent ECharts default legend toggle (we handle it ourselves)
      this.chartInstance.setOption({
        legend: { selected: { [params.name]: true } }
      });
      this.onLegendItemClick(params.name);
    });
  }

  // ==================== CROSS-FILTERING METHODS ====================

  /**
   * Called when user clicks a legend item.
   * Supports multi-select toggle when isMultiSelect is true.
   */
  onLegendItemClick(legendName: string): void {
    if (!this._data?.enableLegendClick) return;

    if (this._data?.isMultiSelect) {
      const idx = this.selectedLegends.indexOf(legendName);
      if (idx === -1) {
        this.selectedLegends = [...this.selectedLegends, legendName];
      } else {
        this.selectedLegends = this.selectedLegends.filter(l => l !== legendName);
      }
    } else {
      if (this.selectedLegends.length === 1 && this.selectedLegends[0] === legendName) {
        this.selectedLegends = [];
      } else {
        this.selectedLegends = [legendName];
      }
    }

    if (this.selectedLegends.length === 0) {
      this.broadcastLegendFilter([]);
    } else {
      this.broadcastLegendFilter(this.selectedLegends);
    }

    // Reload self with updated filters
    this.reloadSelf();
  }

  /**
   * Called when user clicks a y-axis label (category) or bar.
   * Supports multi-select toggle when isMultiSelect is true.
   */
  protected handleLabelClick(clickedCategory: string): void {
    if (!clickedCategory || !this._data?.enableLabelClick) return;
    const clickedStr = String(clickedCategory);

    if (this._data?.isMultiSelect) {
      const idx = this.selectedCategories.findIndex(c => String(c) === clickedStr);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedStr];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => String(c) !== clickedStr);
      }
    } else {
      if (this.selectedCategories.length === 1 && String(this.selectedCategories[0]) === clickedStr) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedStr];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.broadcastCategoryFilter([]);
    } else {
      this.broadcastCategoryFilter(this.selectedCategories);
    }

    // Reload self with updated filters
    this.reloadSelf();
  }

  /**
   * Reload self with current filters so API call goes out.
   */
  protected reloadSelf(): void {
    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          if (!res) return;
          const chartData = res?.data || res;
          if (chartData) this.initChart(chartData);
        },
        error: () => { /* ignore */ }
      });
  }

  /**
   * Broadcast legend selection to listener charts.
   * 1) Emits legendSync for visual dimming
   * 2) Emits regular filter update for API calls
   */
  protected broadcastLegendFilter(legends: string[]): void {
    const listenerConfig =
      this._data?.legendListener ||
      this._data?.legendListner ||
      this._data?.filterListener ||
      this._data?.filterListner;

    const legendKey = this._data?.legendKey || this._data?.filterKey;

    // Step 1: Emit legendSync for visual dimming
    const allSeriesNames = [
      this.allSeriesData?.cumminsLabel || '',
      this.allSeriesData?.nonCumminsLabel || ''
    ].filter(Boolean);
    const selectedIndices = legends.map(l => allSeriesNames.indexOf(l)).filter(i => i !== -1);
    const selectedColors = legends.map(l => {
      if (l === this.allSeriesData?.cumminsLabel) return this.allSeriesData?.cumminsColor || '';
      if (l === this.allSeriesData?.nonCumminsLabel) return this.allSeriesData?.nonCumminsColor || '';
      return '';
    }).filter(Boolean);
    const colorSyncEnabled = this._data?.chartConfig?.colorSyncEnabled === true;

    const legendSyncData = buildLegendSyncData(
      selectedIndices,
      allSeriesNames,
      this._data.widgetId,
      listenerConfig,
      { colorSyncEnabled, selectedColors, legendKey },
    );
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: Emit regular filter update for API calls on listener charts
    if (legendKey) {
      if (legends.length > 0) {
        this.globalFilterService.updateFilterSelection(
          legendKey, legends, false, this._data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
      }
    }
  }

  /**
   * Broadcast category (label) selection to listener charts.
   * 1) Emits legendSync for visual dimming
   * 2) Emits regular filter update for API calls
   */
  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this._data?.labelListener ||
      this._data?.legendListener ||
      this._data?.filterListener ||
      this._data?.filterListner;

    const labelKey = this._data?.labelKey || this._data?.filterKey;

    // Step 1: Emit legendSync for visual dimming
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this._data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: Emit regular filter update for API calls
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this._data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(labelKey, false, listenerConfig);
      }
    }
  }

  /**
   * Handle incoming legendSync from another chart (visual dimming).
   */
  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this._data?.widgetId) return;
    if (!this.chartInstance) return;

    // Reset if empty selection
    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.selectedLegends = [];
      this.resetHighlight();
      return;
    }

    if (filters.isCategoryFilter && filters.selectedLegends?.length > 0) {
      const matchMode = (this._data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = this.allCategories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
    } else if (filters.selectedLegends?.length > 0) {
      // Try matching against series names (Cummins / Non Cummins)
      const seriesNames = [
        this.allSeriesData?.cumminsLabel || '',
        this.allSeriesData?.nonCumminsLabel || ''
      ].filter(Boolean);
      const matchMode = (this._data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingSeries = seriesNames.filter((name: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(name, legend, matchMode))
      );
      if (matchingSeries.length > 0) {
        this.highlightSeriesByNames(matchingSeries);
      } else {
        // Try matching categories
        const matchingCategories = this.allCategories.filter((cat: string) =>
          filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
        );
        if (matchingCategories.length > 0) {
          this.highlightCategories(matchingCategories);
        }
      }
    }
  }

  // ==================== VISUAL HIGHLIGHTING ====================

  /**
   * Dim all categories except the selected ones.
   * In this horizontal chart, categories are on the y-axis (reversed for display).
   */
  protected highlightCategories(categories: string[]): void {
    if (!this.chartInstance) return;
    const option = this.chartInstance.getOption() as any;
    if (!option?.series) return;

    // The displayed categories are reversed, so find indices in the reversed display array
    const displayReversed = (this.visibleCategories || []).slice().reverse();
    const matchIndices = categories
      .map((cat: string) => displayReversed.findIndex(c => String(c) === String(cat)))
      .filter((i: number) => i !== -1);

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      if (!s.data) return s;
      const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
      const newData = dataArr.map((val: any, idx: number) => {
        const isMatch = matchIndices.includes(idx);
        const opacity = isMatch ? 1 : 0.2;
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity } };
        }
        return { value: val, itemStyle: { opacity } };
      });
      return { ...s, data: newData };
    });

    this.chartInstance.setOption({ series: updatedSeries });
  }

  /**
   * Dim all series except those whose names are in the given list.
   */
  protected highlightSeriesByNames(names: string[]): void {
    if (!this.chartInstance) return;
    const option = this.chartInstance.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: names.includes(seriesName) ? 1 : 0.2 } };
      }
      return s;
    });

    this.chartInstance.setOption({ series: updatedSeries });
  }

  /**
   * Reset all visual highlighting.
   */
  protected resetHighlight(): void {
    if (!this.chartInstance) return;
    const option = this.chartInstance.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        if (!s.data) return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 } };
        const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
        const newData = dataArr.map((val: any) => {
          if (typeof val === 'object' && val !== null) {
            return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity: 1 } };
          }
          return { value: val, itemStyle: { opacity: 1 } };
        });
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 }, data: newData };
      }
      return s;
    });

    this.chartInstance.setOption({ series: updatedSeries });
  }

  // --- Scrollbar / Navigation ---
  previousSlide(): void {
    if (this.sliderIndex > 0) {
      this.sliderIndex--;
      this.updateVisibleData();
      if (this.allSeriesData) this.renderChartWithVisibleData(this.allSeriesData.cumminsLabel, this.allSeriesData.nonCumminsLabel, this.allSeriesData.cumminsColor, this.allSeriesData.nonCumminsColor);
    }
  }

  nextSlide(): void {
    if (this.sliderIndex + this.maxVisibleBars < this.allCategories.length) {
      this.sliderIndex++;
      this.updateVisibleData();
      if (this.allSeriesData) this.renderChartWithVisibleData(this.allSeriesData.cumminsLabel, this.allSeriesData.nonCumminsLabel, this.allSeriesData.cumminsColor, this.allSeriesData.nonCumminsColor);
    }
  }

  onScrollbarMouseDown(event: MouseEvent): void {
    event.preventDefault();
    if (this.allCategories.length <= this.maxVisibleBars) return;
    this.isScrollbarDragging = true;
    this.scrollbarStartY = event.clientY;
    this.scrollStartOffset = this.sliderIndex;
    this.dragHandler = (e: MouseEvent) => this.onScrollbarDrag(e);
    this.upHandler = () => this.onScrollbarMouseUp();
    document.addEventListener('mousemove', this.dragHandler!);
    document.addEventListener('mouseup', this.upHandler!);
  }

  getScrollbarThumbHeight(): number {
    if (this.allCategories.length <= this.maxVisibleBars) return 0;
    return (this.maxVisibleBars / this.allCategories.length) * this.visibleHeight;
  }

  getScrollbarThumbTop(): number {
    if (this.allCategories.length <= this.maxVisibleBars) return 0;
    const maxScroll = this.allCategories.length - this.maxVisibleBars;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = this.getScrollbarThumbHeight();
    const maxThumbTravel = scrollableHeight - thumbHeight;
    return (this.sliderIndex / maxScroll) * maxThumbTravel;
  }

  protected onScrollbarDrag(event: MouseEvent): void {
    if (!this.isScrollbarDragging) return;
    const deltaY = event.clientY - this.scrollbarStartY;
    const maxScroll = this.allCategories.length - this.maxVisibleBars;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = (this.maxVisibleBars / this.allCategories.length) * scrollableHeight;
    const maxThumbTravel = scrollableHeight - thumbHeight;
    if (maxThumbTravel <= 0) return;
    const startThumbY = (this.scrollStartOffset / Math.max(1, maxScroll)) * maxThumbTravel;
    const newThumbY = startThumbY + deltaY;
    const clampedThumbY = Math.max(0, Math.min(newThumbY, maxThumbTravel));
    const newOffset = Math.round((clampedThumbY / maxThumbTravel) * maxScroll);
    if (newOffset !== this.sliderIndex && newOffset >= 0 && newOffset <= maxScroll) {
      this.sliderIndex = newOffset;
      this.updateVisibleData();
      if (this.allSeriesData) this.renderChartWithVisibleData(this.allSeriesData.cumminsLabel, this.allSeriesData.nonCumminsLabel, this.allSeriesData.cumminsColor, this.allSeriesData.nonCumminsColor);
    }
  }

  protected onScrollbarMouseUp(): void {
    this.isScrollbarDragging = false;
    if (this.dragHandler) {
      document.removeEventListener('mousemove', this.dragHandler);
      this.dragHandler = null;
    }
    if (this.upHandler) {
      document.removeEventListener('mouseup', this.upHandler);
      this.upHandler = null;
    }
  }

  // Format numeric values for labels
  protected formatLabelValue(v: any): string {
    const n = Number(v) || 0;
    const abs = Math.abs(n);
    if (abs >= 1000000) return `${Math.round(n / 1000000)}M`;
    if (abs >= 1000) return `${Math.round(n / 1000)}K`;
    return `${Math.round(n)}`;
  }
}
